import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {PageNameResponse} from '@services/associations/model/pageNameResponse';
import {OAuth2TokensDto} from '@services/authentication/model/oAuth2TokensDto';
import {PaginationModel} from '@shared/models/pagination.model';
import {Observable} from 'rxjs';
import {AssociationSearchRequest} from "@services/back-office/model/associationSearchRequest";
import {PageAssociationListResponse} from "@services/back-office/model/pageAssociationListResponse";
import {AssociationListResponse} from '@services/back-office/model/associationListResponse';
import {PageOrganizationManagerModel} from "@services/back-office/model/pageOrganizationManagerModel";
import {OrganizationManagerSearchModel} from "@services/back-office/model/organizationManagerSearchModel";
import {OrganizationManagerModel} from "@services/back-office/model/organizationManagerModel";
// import {ImportCounterModel} from "@services/back-office/model/importCounterModel";
import {FileModel} from "@services/accounting/model/fileModel";
import {PageAssociationListSimpleResponse} from "@services/back-office/model/pageAssociationListSimpleResponse";
import {OrganizationTariffModel} from "@services/back-office/model/organizationTariffModel";
import {RoleModel} from "@services/back-office/model/roleModel";
import {UserModel} from "@services/back-office/model/userModel";
import {GovernmentModel} from "@services/back-office/model/governmentModel";
import {OrganizationLocationData} from '@services/back-office/model/organizationLocationData';
import {StringRequest} from "@services/accounting/model/stringRequest";
import {PageBaseNameResponse} from "@services/back-office/model/pageBaseNameResponse";
import {OrganizationBuildingModel} from '@services/back-office/model/organizationBuildingModel';
import {OrganizationRequest} from '@services/back-office/model/organizationRequest';
import {OrganizationSectionModel} from '@services/back-office/model/organizationSectionModel';

@Injectable({
    providedIn: 'root'
})
export class AssociationsService {
    private baseUrl = environment.baseUrl;

    constructor(
        private http: HttpClient,
    ) {
    }

    /** Load Associations */
    loadAssociations(request: AssociationSearchRequest, pagination: PaginationModel): Observable<PageAssociationListResponse | PageNameResponse> {
        const url = `${this.baseUrl}/back-office/v1/organization/search?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageAssociationListResponse | PageNameResponse>(url, request);
    }

    searchAssociation(request: AssociationSearchRequest, pagination: PaginationModel): Observable<PageAssociationListSimpleResponse> {
        const url = `${this.baseUrl}/back-office/v1/organization/search/simple?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageAssociationListSimpleResponse>(url, request);
    }

    getAssociation(associationId: string): Observable<AssociationListResponse> {
        const url = `${this.baseUrl}/back-office/v1/organization/${associationId}`;
        return this.http.get<AssociationListResponse>(url);
    }

    loadRoles(associationId: string): Observable<GovernmentModel[]> {
        const url = `${this.baseUrl}/back-office/v1/organization/government/${associationId}`;
        return this.http.get<GovernmentModel[]>(url);
    }

    loadTariffs(associationId: string): Observable<OrganizationTariffModel[]> {
        const url = `${this.baseUrl}/back-office/v1/organization/${associationId}/tariff/history`;
        return this.http.get<OrganizationTariffModel[]>(url);
    }


    loadBuildings(associationId: string): Observable<OrganizationBuildingModel[]> {
        const url = `${this.baseUrl}/back-office/v1/building/${associationId}`;
        return this.http.get<OrganizationBuildingModel[]>(url);
    }

    /** Add Associations */
    addAssociations(request: OrganizationRequest): Observable<AssociationListResponse> {
        const url = `${this.baseUrl}/back-office/v1/organization`;
        return this.http.post<AssociationListResponse>(url, request);
    }

    /** Edit Associations */
    editAssociations(associationId: string, request: OrganizationRequest): Observable<AssociationListResponse> {
        const url = `${this.baseUrl}/back-office/v1/organization/${associationId}`;
        return this.http.put<AssociationListResponse>(url, request);
    }

    blockAssociations(organizationId: string): Observable<AssociationListResponse> {
        const url = `${this.baseUrl}/back-office/v1/organization/${organizationId}`;
        return this.http.delete<AssociationListResponse>(url);
    }

    deleteApartments(associationId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/organization/${associationId}/delete/apartments`;
        return this.http.delete<void>(url);
    }

    deleteTenants(associationId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/organization/${associationId}/delete/tenants`;
        return this.http.delete<void>(url);
    }

    /** Add Building */
    addBuilding(organizationId: string, request: OrganizationBuildingModel): Observable<OrganizationBuildingModel> {
        const url = `${this.baseUrl}/back-office/v1/building/${organizationId}`;
        return this.http.post<OrganizationBuildingModel>(url, request);
    }

    /** Edit Building */
    editBuilding(buildingId: string, request: OrganizationBuildingModel): Observable<OrganizationBuildingModel> {
        const url = `${this.baseUrl}/back-office/v1/building/${buildingId}`;
        return this.http.put<OrganizationBuildingModel>(url, request);
    }

    /** Delete Building */
    deleteBuilding(buildingId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/building/${buildingId}`;
        return this.http.delete<void>(url);
    }

    /** Add main admin government */
    addAdminGovernment(organizationId: string, request: UserModel) {
        const url = `${this.baseUrl}/back-office/v1/organization/government/admin/${organizationId}`;
        return this.http.post<void>(url, request);
    }

    /** Add hidden role government */
    addHidenGovernment(organizationId: string, userId: string, roleId: string) {
        const url = `${this.baseUrl}/back-office/v1/organization/government/hidden/${organizationId}/${userId}/${roleId}`;
        return this.http.post<void>(url, {});
    }

    /** Delete government */
    deleteGovernment(roleId: string) {
        const url = `${this.baseUrl}/back-office/v1/organization/government/${roleId}`;
        return this.http.delete<void>(url);
    }

    /** Get assotiation roles & possitions */
    getAssociationRoles(organizationId: string): Observable<RoleModel[]> {
        const url = `${this.baseUrl}/back-office/v1/organization/government/roles/${organizationId}`;
        return this.http.get<RoleModel[]>(url);
    }

    getAssociationPositions(associationId: string, query?: string): Observable<string[]> {
        const url = `${this.baseUrl}/back-office/v1/organization/government/${associationId}/list/position`;
        return this.http.post<string[]>(url, {query});
    }

    /** Get temporary token for transition into Cabinet */
    getTempToken(associationId: string): Observable<OAuth2TokensDto> {
        const url = `${environment.baseUrl}/authentication/cabinet/${associationId}/login`;
        return this.http.get<OAuth2TokensDto>(url);
    }

    /** Add Tariff */
    addTariff(associationId: string | undefined, tariff: any): Observable<OrganizationTariffModel> {
        const url = `${this.baseUrl}/back-office/v1/organization/${associationId}/tariff/${tariff}`;
        return this.http.get<OrganizationTariffModel>(url);
    }

    /** Upload data NEW */
    checkApartmentData(associationId: string, sheetId: string, upload: boolean, sortBySection: boolean, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/organization/v1/apartment/sheets/fill/${associationId}/${sheetId}?upload=${upload}&sortBySection=${sortBySection}&buildingId=${buildingId}`;
        return this.http.get<void>(url);
    }

    uploadApartmentData(associationId: string, sheetId: string, sortBySection: boolean, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/organization/v1/apartment/sheets/read/${associationId}/${sheetId}?sortBySection=${sortBySection}&buildingId=${buildingId}`;
        return this.http.get<void>(url);
    }

    fillUserApartmentData(associationId: string, sheetId: string, sortBySection: boolean, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/organization/v1/apartment/tenant/sheets/fill/${associationId}/${sheetId}?sortBySection=${sortBySection}&buildingId=${buildingId}`;
        return this.http.get<void>(url);
    }

    uploadUserApartmentDataData(associationId: string, sheetId: string, sortBySection: boolean, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/organization/v1/apartment/tenant/sheets/read/${associationId}/${sheetId}?sortBySection=${sortBySection}&buildingId=${buildingId}`;
        return this.http.get<void>(url);
    }

    fillTenantsData(associationId: string, sheetId: string, sortBySection: boolean, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/organization/v1/tenant/sheets/fill/${associationId}/${sheetId}?sortBySection=${sortBySection}&buildingId=${buildingId}`;
        return this.http.get<void>(url);
    }

    uploadTenantsData(associationId: string, sheetId: string, sortBySection: boolean, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/organization/v1/tenant/sheets/read/${associationId}/${sheetId}?sortBySection=${sortBySection}&buildingId=${buildingId}`;
        return this.http.get<void>(url);
    }

    fillBalancesDataNew(associationId: string, sheetId: string, sortBySection: boolean, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/accrual/v1/initial/apartment/sheets/fill/${associationId}/${sheetId}?sortBySection=${sortBySection}${buildingId ? '&buildingId=' + buildingId : ''}`;
        return this.http.get<void>(url);
    }

    uploadBalancesDataNew(associationId: string, sheetId: string, sortBySection: boolean, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/accrual/v1/initial/apartment/sheets/read/${associationId}/${sheetId}?sortBySection=${sortBySection}${buildingId ? '&buildingId=' + buildingId : ''}`;
        return this.http.get<void>(url);
    }

    fillCountersDataNew(associationId: string, sheetId: string, sortBySection: boolean, resource: string, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/accrual/v1/counter/apartment/sheets/fill/${associationId}/${sheetId}/${resource}?sortBySection=${sortBySection}${buildingId ? '&buildingId=' + buildingId : ''}`;
        return this.http.get<void>(url);
    }

    uploadCountersDataNew(associationId: string, sheetId: string, sortBySection: boolean, resource: string, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/accrual/v1/counter/apartment/sheets/read/${associationId}/${sheetId}/${resource}?sortBySection=${sortBySection}${buildingId ? '&buildingId=' + buildingId : ''}`;
        return this.http.get<void>(url);
    }

    /** Upload data */
    fillBalancesData(sheetId: string, sortBySection: boolean, associationId: string, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/payments/import/fill/${sheetId}?sortBySection=${sortBySection}&associationId=${associationId}${buildingId ? '&buildingId=' + buildingId : ''}`;
        return this.http.get<void>(url);
    }

    checkBalancesData(sheetId: string, uploadFlag: boolean, sortBySection: boolean, associationId: string, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/payments/import/check/${sheetId}?upload=${uploadFlag}&sortBySection=${sortBySection}&associationId=${associationId}${buildingId ? '&buildingId=' + buildingId : ''}`;
        return this.http.get<void>(url);
    }

    fillCountersData(sheetId: string, period: string, sortBySection: boolean, associationId: string, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/management/import/fill/${sheetId}/${period}/?sortBySection=${sortBySection}&associationId=${associationId}${buildingId ? '&buildingId=' + buildingId : ''}`;
        return this.http.get<void>(url);
    }

    checkCountersData(sheetId: string, period: string, uploadFlag: boolean, sortBySection: boolean, associationId: string, buildingId?: string): Observable<void> {
        const url = `${environment.baseUrl}/management/import/check/${sheetId}/${period}/?upload=${uploadFlag}&sortBySection=${sortBySection}&associationId=${associationId}${buildingId ? '&buildingId=' + buildingId : ''}`;
        return this.http.get<void>(url);
    }

    /** Search Apartments */
    searchApartments(organizationId: string, request: StringRequest, pagination: PaginationModel): Observable<PageBaseNameResponse> {
        const url = `${environment.baseUrl}/back-office/v1/organization/${organizationId}/apartments/?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageBaseNameResponse>(url, request);
    }

    /** Sections */
    loadSections(associationId: string): Observable<OrganizationSectionModel[]> {
        const url = `${environment.baseUrl}/back-office/v1/section/${associationId}/sections`;
        return this.http.get<OrganizationSectionModel[]>(url);
    }

    addSection(organizationId: string, request: OrganizationSectionModel): Observable<OrganizationSectionModel> {
        const url = `${environment.baseUrl}/back-office/v1/section/${organizationId}/section`;
        return this.http.post<OrganizationSectionModel>(url, request);
    }

    getSection(sectionId: string): Observable<OrganizationSectionModel> {
        const url = `${environment.baseUrl}/back-office/v1/section/${sectionId}`;
        return this.http.get<OrganizationSectionModel>(url);
    }

    editSection(sectionId: string, request: OrganizationSectionModel): Observable<OrganizationSectionModel> {
        const url = `${environment.baseUrl}/back-office/v1/section/${sectionId}`;
        return this.http.put<OrganizationSectionModel>(url, request);
    }

    deleteSection(sectionId: string): Observable<void> {
        const url = `${environment.baseUrl}/back-office/v1/section/${sectionId}`;
        return this.http.delete<void>(url);
    }

    uploadFile(data: any): Observable<FileModel> {
        const url = `${environment.baseUrl}/organization/v1/storage/upload/public`;
        return this.http.post<FileModel>(url, data);
    }

    getOrganizationManagers(request: OrganizationManagerSearchModel): Observable<PageOrganizationManagerModel> {
        const url = `${environment.baseUrl}/back-office/v1/manager/organization/list`;
        return this.http.post<PageOrganizationManagerModel>(url, request);
    }

    addOrganizationManager(request: OrganizationManagerModel): Observable<OrganizationManagerModel> {
        const url = `${environment.baseUrl}/back-office/v1/manager/organization`;
        return this.http.post<OrganizationManagerModel>(url, request);
    }

    deleteOrganizationManager(organizationManagerId: string): Observable<void> {
        const url = `${environment.baseUrl}/back-office/v1/manager/organization/${organizationManagerId}`;
        return this.http.delete<void>(url);
    }

    uploadCompaniesClassifier(data: any): Observable<void> {
        const url = `${environment.baseUrl}/back-office/v1/classifier/company/update`;
        return this.http.post<void>(url, data);
    }

    // uploadCompaniesClassifierState(): Observable<ImportCounterModel> {
    //     const url = `${environment.baseUrl}/back-office/v1/classifier/company/status`;
    //     return this.http.get<ImportCounterModel>(url);
    // }

    getLocation(id: string, byBuilding?: boolean): Observable<OrganizationLocationData> {
        const url = `${environment.baseUrl}/back-office/v1/organization/location/${byBuilding ? 'building/' : ''}${id}`;
        return this.http.get<OrganizationLocationData>(url);
    }

    setLocation(request: OrganizationLocationData): Observable<OrganizationLocationData> {
        const url = `${environment.baseUrl}/back-office/v1/organization/location`;
        return this.http.put<OrganizationLocationData>(url, request);
    }
}
